/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { Dispatch, MutableRef, StateUpdater } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  Cell,
  RowActionDetail,
  ColumnResizingDetail,
  ColumnResizeDetail,
  TableSelectionDetail,
  TableSelectionMode
} from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ActiveCell,
  ActiveCellDetail,
  AppliedColumnWidths,
  ColumnHeaderFooterInfo,
  ColumnResizeInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { useCallback, useRef, useState } from 'preact/hooks';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { clearElementSelection, getIsSelectionPending } from '#utils/PRIVATE_textSelectionUtils';
import { handleRowActionGesture } from '#PRIVATE_TableCommon/utils/ActionUtils';
import { isLogicalCtrl } from '#PRIVATE_TableCommon/utils/EventUtils';
import { getActionContext } from '../utils/TableActionUtils';
import { logicalCellExtractor, TABLE_ROW_SELECTOR } from '../utils/TableDomUtils';
import {
  handleActiveColumnHeaderFooterGesture,
  handleEndInteractionColumnHeaderFooterGesture,
  handleOverColumnHeaderFooterGesture
} from '../utils/TableInteractionUtils';
import {
  handleRangeSelectionGesture,
  handleSelectionGesture,
  isRangeSelectionGesture
} from '../utils/TableSelectionUtils';
import {
  handleColumnResizeEndGesture,
  handleColumnResizeStartGesture,
  handleColumnResizingGesture,
  handleEnterResizingGesture,
  handleLeaveResizingGesture,
  handleOverResizeRegionGesture
} from '../utils/TableSizingUtils';
import {
  handleLeaveTableTooltipGesture,
  handleMoveTooltipGesture
} from '../utils/TableTooltipUtils';

type PointerInfo = {
  target: EventTarget | null;
  x: number;
  y: number;
};

type UsePointerHandlingOptions<K, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  tableId: string;
  tableData: CollectionData<K, D>[];
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  appliedColumnWidths?: AppliedColumnWidths<C>;
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  onColumnResizing?: (detail: ColumnResizingDetail<C>) => void;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  activeCell?: ActiveCell<K, C>;
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void;
  isDropDownSelection: boolean;
  selectionMode?: TableSelectionMode;
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onTableSelectionChange?: (
    detail: TableSelectionDetail<K, C>,
    isSelectionExtension: boolean
  ) => void;
  activeStyleColumnInfo: ColumnHeaderFooterInfo<C> | undefined;
  setActiveStyleColumnInfo: Dispatch<StateUpdater<ColumnHeaderFooterInfo<C> | undefined>>;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  showTooltip: (cell: Cell<K, C>) => void;
  hideTooltip: (isImmediate?: boolean) => void;
  tooltipId: string;
};

/**
 * Hook that manages pointer interactions on the Preact Table
 */
export function usePointerHandling<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  tableId,
  tableData,
  isRtl,
  columnsArray,
  appliedColumnWidths,
  resizeInfoRef,
  selectionExtensionElementRef,
  onColumnResizing,
  onColumnResize,
  activeCell,
  onActiveCellChanged,
  isDropDownSelection,
  selectionMode,
  selected,
  onTableSelectionChange,
  activeStyleColumnInfo,
  setActiveStyleColumnInfo,
  onRowAction,
  showTooltip,
  hideTooltip,
  tooltipId
}: UsePointerHandlingOptions<K, D, C>) {
  // tracks whether the most recent 'pointerdown' event was due to a touch gesture
  const selectionTouchRef = useRef<boolean>(false);

  // tracks the column key for any header or footer being hovered over
  const [hoverStyleColumnInfo, setHoverStyleColumnInfo] = useState<ColumnHeaderFooterInfo<C>>();

  // tracks the current 'pointer over' element for comparison during 'click' event handling to
  // prevent screen readers from triggering undesired selection gestures from simulated events
  const pointerInfoRef = useRef<PointerInfo>();

  // Pointer move handler for the outer Table.
  const _pointerMoveHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null) {
        pointerInfoRef.current = { target: event.target, x: event.clientX, y: event.clientY };
        handleMoveTooltipGesture(tableId, columnsArray, event, showTooltip, hideTooltip, tooltipId);
        if (onColumnResizing != null) {
          if (!resizeInfoRef.current.isResizing) {
            handleOverResizeRegionGesture(
              rootElement,
              tableId,
              event,
              columnsArray,
              resizeInfoRef,
              isRtl
            );
          } else if (scrollerElement != null && appliedColumnWidths != null) {
            handleColumnResizingGesture(
              scrollerElement,
              event,
              appliedColumnWidths,
              resizeInfoRef,
              isRtl,
              onColumnResizing
            );
          }
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      onColumnResizing,
      showTooltip,
      hideTooltip,
      tooltipId
    ]
  );

  // Pointer over handler for the outer Table.
  const _pointerOverHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        pointerInfoRef.current = { target: event.target, x: event.clientX, y: event.clientY };
        handleOverColumnHeaderFooterGesture(
          tableId,
          event,
          setHoverStyleColumnInfo,
          hoverStyleColumnInfo
        );
      }
    },
    [rootRef, tableId, hoverStyleColumnInfo]
  );

  // Pointer down handler for the outer Table. It ensures the area targeted is 'current'.
  // NOTE - This event handling is triggered prior to 'onFocus' handling and ensures the
  // 'pending current' area is set when an initial pointer interaction focuses the Table.
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      handleActiveColumnHeaderFooterGesture(
        tableId,
        event,
        setActiveStyleColumnInfo,
        activeStyleColumnInfo
      );
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null) {
        selectionTouchRef.current = event.pointerType === 'touch';
        const targetElement = event.target as HTMLElement;
        if (
          onTableSelectionChange != null &&
          activeCell != null &&
          event.shiftKey &&
          isRangeSelectionGesture(
            rootElement,
            tableId,
            columnsArray,
            targetElement,
            activeCell,
            selectionMode
          )
        ) {
          // prevent focus change on selection extension gesture
          if (!getIsSelectionPending()) {
            event.preventDefault();
          }
        } else if (
          scrollerElement != null &&
          appliedColumnWidths != null &&
          handleColumnResizeStartGesture(
            rootElement,
            scrollerElement,
            tableId,
            event,
            columnsArray,
            appliedColumnWidths,
            resizeInfoRef,
            isRtl,
            hideTooltip,
            onColumnResizing
          )
        ) {
          // prevent focus change on column resize gesture
          event.preventDefault();
          resizeInfoRef.current.ignoreClick = true;
        } else if (logicalCellExtractor<K, D, C>(targetElement, tableId, columnsArray) == null) {
          // prevent focus shift when interacting with 'non-focusable' regions (ie - scrollbar)
          event.preventDefault();
        } else {
          // in firefox, ctrl/cmd + click triggers browser selection of <tr> elements, which
          // conflicts with our multiple row selection gesture. to prevent this, schedule a
          // macro-task to remove any node-based selection to ensure there is no 'pending'
          // browser selection found during eventual click event handling
          if (getClientHints().browser === 'firefox' && isLogicalCtrl(event)) {
            setTimeout(() => {
              clearElementSelection();
            }, 0);
          }
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      activeStyleColumnInfo,
      setActiveStyleColumnInfo,
      isRtl,
      hideTooltip,
      activeCell,
      selectionMode,
      onTableSelectionChange,
      onColumnResizing
    ]
  );

  // Pointer up handler for the outer Table.
  const _pointerUpHandler = useCallback(
    (event: PointerEvent) => {
      handleEndInteractionColumnHeaderFooterGesture(
        setActiveStyleColumnInfo,
        activeStyleColumnInfo
      );
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (scrollerElement != null && appliedColumnWidths != null) {
        handleColumnResizeEndGesture(
          scrollerElement,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [
      viewportInfo,
      appliedColumnWidths,
      resizeInfoRef,
      activeStyleColumnInfo,
      setActiveStyleColumnInfo,
      isRtl,
      onColumnResize
    ]
  );

  // Pointer leave handler for the outer Table.
  const _pointerLeaveHandler = useCallback(
    (event: PointerEvent) => {
      handleEndInteractionColumnHeaderFooterGesture(setHoverStyleColumnInfo, hoverStyleColumnInfo);
      handleEndInteractionColumnHeaderFooterGesture(
        setActiveStyleColumnInfo,
        activeStyleColumnInfo
      );
      const rootElement = rootRef.current;
      if (rootElement != null) {
        handleLeaveResizingGesture(rootElement, tableId, resizeInfoRef);
        handleLeaveTableTooltipGesture(rootElement, event, hideTooltip, tooltipId);
      }
      pointerInfoRef.current = undefined;
    },
    [
      rootRef,
      tableId,
      resizeInfoRef,
      activeStyleColumnInfo,
      setActiveStyleColumnInfo,
      hoverStyleColumnInfo,
      hideTooltip,
      tooltipId
    ]
  );

  // Pointer enter handler for the outer Table.
  const _pointerEnterHandler = useCallback(
    (event: PointerEvent) => {
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (scrollerElement != null && appliedColumnWidths != null) {
        handleEnterResizingGesture(
          scrollerElement,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [viewportInfo, appliedColumnWidths, resizeInfoRef, isRtl, onColumnResize]
  );

  // Click handler for the outer Table. It ensures the Table's selection state is
  // updated to reflect the area targeted, and any action events are fired correctly.
  const _clickHandler = useCallback(
    (event: PointerEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        // only handle click events that match the latest mouse over/move info to prevent screen readers
        // from triggering undesired selections due to simulated click events when transferring focus
        if (
          selectionTouchRef.current ||
          (pointerInfoRef.current != null &&
            pointerInfoRef.current.target === event.target &&
            Math.abs(pointerInfoRef.current.x - event.clientX) <= 1 &&
            Math.abs(pointerInfoRef.current.y - event.clientY) <= 1)
        ) {
          let isSelectionExtension = false;
          const target = event.target as HTMLElement;
          if (
            onTableSelectionChange != null &&
            !resizeInfoRef.current.ignoreClick &&
            !getIsSelectionPending()
          ) {
            if (activeCell != null && event.shiftKey) {
              isSelectionExtension = handleRangeSelectionGesture(
                rootElement,
                tableId,
                columnsArray,
                target,
                activeCell,
                onActiveCellChanged,
                selectionExtensionElementRef,
                onTableSelectionChange,
                isDropDownSelection,
                selectionMode,
                selected
              );
            }
            if (!isSelectionExtension) {
              handleSelectionGesture(
                rootElement,
                tableId,
                columnsArray,
                target,
                isLogicalCtrl(event) || selectionTouchRef.current,
                onTableSelectionChange,
                isDropDownSelection,
                selectionMode,
                selected
              );
            }
          }
          if (
            onRowAction != null &&
            !getIsSelectionPending() &&
            handleRowActionGesture(
              tableId,
              tableData,
              onRowAction,
              target,
              TABLE_ROW_SELECTOR,
              getActionContext
            )
          ) {
            event.stopPropagation();
          }
        }
      }
      resizeInfoRef.current.ignoreClick = false;
    },
    [
      rootRef,
      tableId,
      columnsArray,
      tableData,
      resizeInfoRef,
      selectionExtensionElementRef,
      activeCell,
      onActiveCellChanged,
      isDropDownSelection,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction
    ]
  );

  return {
    pointerHandlingProps: {
      onPointerOver: _pointerOverHandler,
      onPointerMove: _pointerMoveHandler,
      onPointerDown: _pointerDownHandler,
      onPointerUp: _pointerUpHandler,
      onPointerLeave: _pointerLeaveHandler,
      onPointerEnter: _pointerEnterHandler,
      onClick: _clickHandler
    },
    hoverStyleColumnInfo
  };
}
